// Local Imports
import { exhaust } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import { graphics, schdata as s, layout, model, Rotation } from "GenAlphaModel";

import * as style from "../../style";
import { EntityKind } from "../../entity";
import { Args, Entity } from "../../tree";
import { LayoutEntity } from "./layout";
import { MousePos } from "../../mousepos";
import { DrawTo, MIN_LABEL_SCREEN_PX } from "../../draw";
import type { RedrawRequest } from "../../redraw";
import type { LayoutRender } from "./render";

const table: style.TextStyleTable = {
  family: style.THE_FONT,
  style: "normal",
  weight: 700,
  size: 255,
  fill: style.Color.yellow,
  stroke: style.Special.None,
};

// Semi-transparent highlight overlay for selected layout instances.
// Drawn on top of the sprite-cached content so the sprite cache
// doesn't need to be invalidated on selection changes.
const HIGHLIGHT_FILL = style.Color.new(0, 180, 255, 0.3);
const HIGHLIGHT_STROKE = style.Color.new(0, 180, 255, 0.3);
const HIGHLIGHT_STYLE: style.ShapeStyle = {
  cap: "round",
  join: "round",
  linewidth: 2,
  fill: HIGHLIGHT_FILL,
  stroke: HIGHLIGHT_STROKE,
};

// Dimmer purple overlay for sibling segments — segments that belong to the
// same generator as a selected segment but are not themselves selected.
const SIBLING_HIGHLIGHT_FILL = style.Color.new(128, 0, 180, 0.15);
const SIBLING_HIGHLIGHT_STROKE = style.Color.new(128, 0, 180, 0.25);
const SIBLING_HIGHLIGHT_STYLE: style.ShapeStyle = {
  cap: "round",
  join: "round",
  linewidth: 2,
  fill: SIBLING_HIGHLIGHT_FILL,
  stroke: SIBLING_HIGHLIGHT_STROKE,
};

// Outline overlay style for layout cell outlines.
// Dashed cyan stroke, no fill.
const OUTLINE_STYLE: style.ShapeStyle = {
  fill: style.Special.None,
  stroke: style.Color.cyan,
  linewidth: 2,
  lineDash: [100, 50],
  cap: "butt",
  join: "miter",
};

// Pin highlight style for LVS errors - red circle markers
const PIN_HIGHLIGHT_RADIUS = 75; // nm
const PIN_HIGHLIGHT_FILL = style.Color.new(255, 0, 0, 0.35);
const PIN_HIGHLIGHT_STROKE = style.Color.new(255, 0, 0, 0.9);

/**
 * Draw pin highlight markers (red circles) for highlighted pins, one on each
 * of the pin's shapes.
 * Used by both ScalarInstanceEntity and Segment for LVS error visualization.
 */
function drawPinHighlights(
  to: DrawTo,
  xform: s.Xform,
  pins: ReadonlyArray<schema.layout.LayoutPin>,
  highlightedPins: ReadonlySet<string>
): void {
  if (highlightedPins.size === 0) return;
  to.group(xform, (to) => {
    for (const pin of pins) {
      if (!highlightedPins.has(pin.name)) continue;
      for (const { shape } of pin.shapes) {
        const c = schema.pinShapeCenter(shape);
        const circleBbox = {
          min: { x: c.x - PIN_HIGHLIGHT_RADIUS, y: c.y - PIN_HIGHLIGHT_RADIUS },
          max: { x: c.x + PIN_HIGHLIGHT_RADIUS, y: c.y + PIN_HIGHLIGHT_RADIUS },
        };
        to.ellipse(
          { kind: "ellipse", bbox: circleBbox, filled: true },
          {
            fill: PIN_HIGHLIGHT_FILL,
            stroke: PIN_HIGHLIGHT_STROKE,
            linewidth: 2 / to.scale,
            cap: "round",
            join: "round",
          }
        );
      }
    }
  });
}

// Color palette for abstract floorplan mode.
// Each instance gets a semi-transparent fill from this rotating palette.
const ABSTRACT_PALETTE: Array<style.Color> = [
  style.Color.new(66, 133, 244, 0.4), // blue
  style.Color.new(234, 67, 53, 0.4), // red
  style.Color.new(52, 168, 83, 0.4), // green
  style.Color.new(251, 188, 4, 0.4), // yellow
  style.Color.new(171, 71, 188, 0.4), // purple
  style.Color.new(0, 172, 193, 0.4), // teal
  style.Color.new(255, 112, 67, 0.4), // orange
  style.Color.new(84, 110, 122, 0.4), // blue-grey
  style.Color.new(236, 64, 122, 0.4), // pink
  style.Color.new(63, 81, 181, 0.4), // indigo
  style.Color.new(139, 195, 74, 0.4), // light green
  style.Color.new(255, 193, 7, 0.4), // amber
  style.Color.new(121, 85, 72, 0.4), // brown
  style.Color.new(103, 58, 183, 0.4), // deep purple
  style.Color.new(0, 188, 212, 0.4), // cyan
  style.Color.new(255, 87, 34, 0.4), // deep orange
];

// Matching opaque border colors for abstract floorplan mode.
const ABSTRACT_BORDER_PALETTE: Array<style.Color> = [
  style.Color.new(66, 133, 244, 0.9),
  style.Color.new(234, 67, 53, 0.9),
  style.Color.new(52, 168, 83, 0.9),
  style.Color.new(251, 188, 4, 0.9),
  style.Color.new(171, 71, 188, 0.9),
  style.Color.new(0, 172, 193, 0.9),
  style.Color.new(255, 112, 67, 0.9),
  style.Color.new(84, 110, 122, 0.9),
  style.Color.new(236, 64, 122, 0.9),
  style.Color.new(63, 81, 181, 0.9),
  style.Color.new(139, 195, 74, 0.9),
  style.Color.new(255, 193, 7, 0.9),
  style.Color.new(121, 85, 72, 0.9),
  style.Color.new(103, 58, 183, 0.9),
  style.Color.new(0, 188, 212, 0.9),
  style.Color.new(255, 87, 34, 0.9),
];

// Text style for instance names in abstract floorplan mode.
const ABSTRACT_NAME_TABLE: style.TextStyleTable = {
  family: style.THE_FONT,
  style: "normal",
  weight: 700,
  size: 255,
  fill: style.Color.white,
  stroke: style.Special.None,
};

// Simple string hash for deterministic color assignment from the palette.
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

// Compute the bbox to use when sprite-rasterizing an instance: the union
// of `abs.bbox` and every non-degenerate `abs.blockages[i].bbox`. The
// sprite canvas is sized to this bbox, so anything drawn inside the
// sprite (including blockages whose bbox extends past `abs.bbox`)
// renders without being clipped at the sprite boundary. One concrete
// case where this matters: GDS-imported cells whose stored bbox stamps
// differ from the IDE's recomputed `abs.bbox`. `abs.bbox` itself is
// unchanged — hit testing, label placement, instance bbox aggregation,
// etc. all keep their original semantics.
//
// Degenerate (zero-area) blockages are skipped to avoid pulling the
// union toward the origin — they exist in practice (e.g. top-level
// layouts get auto-stamped with `bbox=(0,0)->(0,0)` when their own
// `abs.bbox` is empty). Mirroring `Bbox.getBboxUnion`'s comment, we
// start from `abs.bbox` rather than `Bbox.empty()` for the same reason.
//
// All `to.instance()` callers must route through this helper so the
// sprite cache (keyed on `layout` alone) stays consistent — two
// different `bbox` args for the same layout would cache once and reuse
// silently.
function spriteBbox(lay: model.layout.Layout): model.Bbox {
  let bbox = lay.abs.bbox;
  for (const blk of lay.abs.blockages) {
    const b = blk.bbox;
    if (b.min.x === b.max.x || b.min.y === b.max.y) continue; // degenerate
    bbox = model.Bbox.encompassingBoth(bbox, b);
  }
  return bbox;
}

// Grid metadata retained from the original ArrayXform so that redraw can
// compute visible row/column ranges for viewport culling instead of
// iterating every segment.
interface ArrayGrid {
  readonly nrows: number;
  readonly ncols: number;
  readonly dx: number;
  readonly dy: number;
  readonly baseLoc: { readonly x: number; readonly y: number };
  // The element bbox in the target layout's local coordinates.
  // Used to compute the world-space footprint of each grid cell.
  readonly elemBbox: model.Bbox;
}

type Inner =
  | {
      kind: "unbound";
      drawer: UnboundInstanceEntity;
    }
  | {
      kind: "scalar";
      drawer: ScalarInstanceEntity;
    }
  | {
      kind: "segments"; //
      drawer: SegmentedInstanceEntity;
    }
  | {
      kind: "array";
      drawer: SegmentedInstanceEntity;
      grid: ArrayGrid;
    };

export class InstanceEntity extends Entity<layout.Instance, LayoutRender> {
  //
  readonly entityKind = EntityKind.LayoutInstance;
  public inner: Inner; // The inner drawer, which is either a scalar or segmented instance entity
  constructor(args: Args<layout.Instance, LayoutRender>, hierLevel: number) {
    super(args);
    this.inner = InstanceEntity.inner(args, hierLevel);
  }

  static inner(
    args: Args<layout.Instance, LayoutRender>,
    hierLevel: number
  ): Inner {
    //
    // FIXME! are the type asserions (noted "<= HERE") great?
    //
    // They're certainly not my favorite! But we NEED them, since
    // (a) the TS compiler is not convinced we really have those sub-types (for some reason; we think it's wrong), and
    // (b) we need further updates to the data object, particluarly the soon to be assigned (and really important!) `id` fields.
    //
    if (args.data.target.kind === "unbound" || !args.data.place) {
      return {
        kind: "unbound",
        drawer: new UnboundInstanceEntity(args, hierLevel),
      };
    }
    const { place } = args.data;
    const { kind } = place;
    switch (kind) {
      case "segments": {
        // Rebuild the data with the narrowed `place`/`target` (destructured to
        // preserve TS's narrowing) so it types as `SegmentedInstance` without a
        // cast — same pattern as the "array" case below.
        const { name, id, params, target } = args.data;
        const segmented: SegmentedInstance = {
          name,
          id,
          params,
          target,
          place,
        };
        return {
          kind: "segments",
          drawer: new SegmentedInstanceEntity(
            { parent: args.parent, data: segmented },
            hierLevel
          ),
        };
      }
      case "array": {
        // Convert array instance to segmented instance for drawing
        // Destructure to preserve TypeScript's narrowing of target to BoundTarget
        const { name, id, params, target } = args.data;
        const { array } = place;
        const segmented: SegmentedInstance = {
          name,
          id,
          params,
          target,
          place: {
            kind: "segments",
            segments: layout.ArrayXform.toSegments(array),
          },
        };
        // Resolve the element bbox from the target layout for viewport culling.
        // BoundTarget carries the resolved Layout object with its abstract bbox.
        const elemBbox = target.layout.abs.bbox;
        return {
          kind: "array",
          drawer: new SegmentedInstanceEntity(
            { parent: args.parent, data: segmented },
            hierLevel,
            true // fromArray — used by Segment.redraw stats bumping
          ),
          grid: {
            nrows: array.nrows,
            ncols: array.ncols,
            dx: array.dx,
            dy: array.dy,
            baseLoc: { x: array.loc.x, y: array.loc.y },
            elemBbox,
          },
        };
      }
      case "scalar": {
        // Rebuild with the narrowed `place`/`target` so it types as
        // `ScalarInstance` without a cast (see the "segments"/"array" cases).
        const { name, id, params, target } = args.data;
        const scalar: ScalarInstance = { name, id, params, target, place };
        return {
          kind: "scalar",
          drawer: new ScalarInstanceEntity(
            { parent: args.parent, data: scalar },
            hierLevel
          ),
        };
      }
      default:
        exhaust(kind);
    }
  }

  // Override highlight to propagate to the inner drawer.
  // The inner drawer (ScalarInstanceEntity, SegmentedInstanceEntity, etc.) is parented
  // to ImplEntity (not to this InstanceEntity) due to how `args.parent` is passed in
  // the static `inner()` factory. So the base-class highlight recursion into `children`
  // does not reach the drawer. We forward it manually here.
  override highlight(state: boolean = true): RedrawRequest {
    this.inner.drawer.highlight(state);
    return super.highlight(state);
  }

  // Highlight a specific pin on this instance (for LVS error visualization).
  // Delegates to ScalarInstanceEntity if that's the inner drawer type.
  highlightPin(pinName: string): RedrawRequest {
    const drawer = this.inner.drawer;
    if ("highlightPin" in drawer && typeof drawer.highlightPin === "function") {
      return drawer.highlightPin(pinName);
    }
    return this.root.requestRedraw();
  }

  // Clear all pin highlights on this instance.
  clearPinHighlights(): RedrawRequest {
    const drawer = this.inner.drawer;
    if (
      "clearPinHighlights" in drawer &&
      typeof drawer.clearPinHighlights === "function"
    ) {
      return drawer.clearPinHighlights();
    }
    return this.root.requestRedraw();
  }

  override update = (data: Partial<layout.Instance>): RedrawRequest => {
    this.data = { ...this.data, ...data };
    // FIXME: this pretty aggressively re-creates the inner drawer every time.
    // Really we just need to when it changes `kind` between segmented and scalar.
    // But, detecting that, meh.
    this.inner = InstanceEntity.inner(
      { parent: this.inner.drawer.parent, data: this.data },
      this.inner.drawer.hierLevel
    );
    // Propagate our highlight state to the newly created inner drawer.
    // Without this, the highlight overlay disappears when the inner drawer
    // is recreated (e.g. on server data refresh) because the new drawer
    // starts un-highlighted even though we're still in the selection set.
    if (this.highlighted) {
      this.inner.drawer.highlight(true);
    }
    return this.inner.drawer.root.requestRedraw();
  };

  // Collect transformed bounding boxes for this instance into `out`.
  // For scalar instances, pushes a single bbox. For segments/array instances,
  // pushes one bbox per segment. Unbound instances contribute nothing.
  // This centralizes the bbox-from-instance logic used by recalcBbox,
  // selectionBbox, duplicateSelected, and selectAllInBbox.
  readonly collectBboxes = (out: Array<model.Bbox>): void => {
    const { inner } = this;
    if (inner.kind === "scalar") {
      const { layout: lay } = inner.drawer.data.target;
      out.push(model.Xform.transformBbox(inner.drawer.xform, lay.abs.bbox));
    } else if (inner.kind === "segments" || inner.kind === "array") {
      for (const seg of inner.drawer.segments) {
        const { layout: lay } = seg.scalarEntity.data.target;
        out.push(
          model.Xform.transformBbox(seg.scalarEntity.xform, lay.abs.bbox)
        );
      }
    }
  };

  // Forward calls to the inner drawer
  readonly toData = (): layout.Instance => this.inner.drawer.toData();
  readonly redraw = (to: DrawTo): unknown => {
    // Propagate highlight to segments so they draw the selection overlay.
    // Set highlighted_ directly to avoid triggering extra redraws.
    if (this.inner.kind === "segments" || this.inner.kind === "array") {
      // Check for per-segment selection tracking.
      // Only consult selectedSegments when the instance is highlighted
      // (selected); when unhighlighting, always clear both flags so we
      // don't depend on the order of selectedSegments.clear() vs
      // highlight(false) in deselect().
      const root = this.root;
      const selectedIndices = this.highlighted
        ? root.selectedSegments.get(this)
        : undefined;

      // For array instances with a viewport, compute visible row/column
      // ranges from the grid metadata and only iterate those segments.
      // This turns O(nrows*ncols) iteration into O(visibleRows*visibleCols).
      //
      // `to.viewport` is always in the correct local coordinate frame here:
      // at the root level, local coords equal world coords (no parent xform);
      // inside a vector-rendered instance, DrawToHtmlCanvas.instance() already
      // inverse-transforms the viewport into the child cell's local frame before
      // invoking this redraw callback. Sprite DrawTos use an infinite viewport,
      // which resolves to null, so the culling block below is skipped.
      if (this.inner.kind === "array" && to.viewport) {
        const { grid, drawer } = this.inner;
        const visibleSegs = arrayVisibleSegments(grid, to.viewport);
        const visibleRows = Math.max(
          0,
          visibleSegs.rowMax - visibleSegs.rowMin + 1
        );
        const visibleCols = Math.max(
          0,
          visibleSegs.colMax - visibleSegs.colMin + 1
        );
        const visibleCount = visibleRows * visibleCols;
        const totalCount = grid.nrows * grid.ncols;
        // Array-instance stats: bump `total`, and classify as fully-culled
        // (no visible segments), clipped (partially visible), or fully
        // visible (neither).
        if (to.stats) {
          to.stats.arrays.total += 1;
          if (visibleCount === 0) {
            to.stats.arrays.fullyCulled += 1;
          } else if (visibleCount < totalCount) {
            to.stats.arrays.clipped += 1;
          }
          // Segment-level stats: `total` covers every segment in the grid.
          // Grid-culled segments (outside the visible rectangle) bump
          // `offScreen` here. Segments that the fast path *does* iterate
          // let `Segment.redraw` decide `drawn` vs `offScreen` based on
          // its own viewport self-cull — we must not pre-bump `drawn`
          // here or segments that self-cull would be double-counted.
          // All segments in this path are array-owned, so they also bump
          // the `segments.array` per-source bucket.
          const gridOffScreen = totalCount - visibleCount;
          to.stats.segments.total += totalCount;
          to.stats.segments.offScreen += gridOffScreen;
          to.stats.segments.array.total += totalCount;
          to.stats.segments.array.offScreen += gridOffScreen;
        }
        for (let row = visibleSegs.rowMin; row <= visibleSegs.rowMax; row++) {
          for (let col = visibleSegs.colMin; col <= visibleSegs.colMax; col++) {
            const segIndex = row * grid.ncols + col;
            if (segIndex >= drawer.segments.length) continue;
            const seg = drawer.segments[segIndex];
            if (selectedIndices) {
              const isSelected = selectedIndices.has(seg.index);
              seg.scalarEntity.setSegmentHighlight(isSelected, !isSelected);
            } else {
              seg.scalarEntity.setSegmentHighlight(this.highlighted, false);
            }
            seg.redraw(to);
          }
        }
        return null;
      }

      // Fallback path: non-array segmented, or array-without-viewport (e.g.
      // inside sprite building). All segments get their redraw called —
      // each one decides for itself whether it drew or self-culled, so we
      // only bump `total` here (both the aggregate and the per-source
      // bucket). `Segment.redraw` bumps `drawn` or `offScreen` per segment.
      if (to.stats) {
        const count = this.inner.drawer.segments.length;
        to.stats.segments.total += count;
        if (this.inner.kind === "array") {
          to.stats.arrays.total += 1;
          to.stats.segments.array.total += count;
        } else {
          to.stats.segments.freeform.total += count;
        }
      }

      for (const seg of this.inner.drawer.segments) {
        if (selectedIndices) {
          // Per-segment selection: only selected segments get blue highlight,
          // sibling segments get purple highlight.
          seg.scalarEntity.setSegmentHighlight(
            selectedIndices.has(seg.index),
            !selectedIndices.has(seg.index)
          );
        } else {
          // Whole-instance selection or unhighlighting: propagate parent state
          seg.scalarEntity.setSegmentHighlight(this.highlighted, false);
        }
      }
    }
    return this.inner.drawer.redraw(to);
  };
}

//
// # Unbound Instances
//
// Instances for which we don't have a layout.
// For now, draw, ... nothing?
//
//
export class UnboundInstanceEntity extends Entity<
  layout.Instance,
  LayoutRender
> {
  constructor(
    args: Args<layout.Instance, LayoutRender>,
    readonly hierLevel: number
  ) {
    super(args);
  }
  override redraw = (_to: DrawTo): unknown => {
    // FIXME: actually draw stuff!
    return null;
  };
  readonly toData = (): layout.Instance => this.data;
}

//
type SegmentedInstance = layout.SegmentedInstance & {
  target: { kind: "bound" };
};
//
export class SegmentedInstanceEntity extends Entity<
  SegmentedInstance,
  LayoutRender
> {
  // Whether this segmented-instance drawer was created from an `ArrayXform`
  // (a regular 2D grid) or from a freeform segment list. Used by
  // `Segment.redraw` to attribute its stats bumps to the right bucket
  // (`stats.segments.array` vs `stats.segments.freeform`).
  constructor(
    args: Args<SegmentedInstance, LayoutRender>,
    readonly hierLevel: number,
    readonly fromArray: boolean = false
  ) {
    super(args);
  }

  // The main thing: the individual scalar instances
  segment = (segOpt: schema.layout.Segment, index: number) => {
    const xform = segOpt ?? layout.LayoutXform.identity();
    // Resolve per-segment target override if present
    const segTarget = segOpt?.target
      ? model.layout.Layout.layouts.get(segOpt.target)
      : undefined;
    return new Segment(this, index, { xform, segTarget }, this.hierLevel);
  };

  readonly segments: Array<Segment> = this.data.place.segments.map(
    this.segment
  );

  get scalarData(): Omit<ScalarInstance, "place"> {
    const { place: _place, ...data } = this.data;
    return data;
  }

  override update = (data: Partial<SegmentedInstance>): RedrawRequest => {
    this.data = { ...this.data, ...data };
    return this.root.requestRedraw();
  };

  override redraw = (to: DrawTo): unknown => {
    this.segments.forEach((seg) => seg.redraw(to));
    return null;
  };

  // Clear pin highlights on all segments.
  // Called via InstanceEntity.clearPinHighlights when LVS highlights are cleared.
  clearPinHighlights = (): RedrawRequest => {
    for (const seg of this.segments) {
      seg.scalarEntity.highlightedPins.clear();
    }
    return this.root.requestRedraw();
  };

  readonly toData = (): SegmentedInstance => {
    return {
      ...this.scalarData,
      place: {
        kind: "segments",
        segments: this.segments.map((seg) => {
          const { xform, segTarget } = seg.data;
          if (segTarget) {
            return { ...xform, target: segTarget.ident };
          }
          return xform;
        }),
      },
    };
  };
}

export class Segment {
  scalarEntity: ScalarInstanceEntity;

  constructor(
    readonly parent: SegmentedInstanceEntity,
    readonly index: number,
    public data: {
      xform: layout.LayoutXform;
      segTarget?: model.layout.Layout;
    },
    readonly hierLevel: number
  ) {
    this.scalarEntity = new ScalarInstanceEntity(
      { data: this.scalarData, parent: parent },
      hierLevel
    );
  }
  get scalarData(): ScalarInstance {
    const parentScalarData = this.parent.scalarData;
    const name = `${parentScalarData.name}.${this.index}`;
    const { segTarget } = this.data;
    // Read live xform from parent data (same logic as getXform, but inline
    // to avoid calling an arrow property that isn't initialized during construction).
    const liveSeg = this.parent.data.place.segments[this.index];
    const xform = liveSeg ?? this.data.xform;
    // Use per-segment target if resolved, otherwise fall back to parent target
    const target = segTarget
      ? { kind: "bound" as const, layout: segTarget }
      : parentScalarData.target;
    return {
      ...parentScalarData,
      target,
      place: { kind: "scalar", xform },
      name,
    };
  }
  update = (data: {
    place: { kind: "scalar"; xform: Partial<layout.LayoutXform> };
  }): RedrawRequest => {
    this.data.xform = { ...this.data.xform, ...data.place.xform };
    // Update the live parent segments array so rendering reflects the change.
    const seg = this.parent.data.place.segments[this.index];
    if (seg) {
      seg.loc = this.data.xform.loc;
      seg.reflect = this.data.xform.reflect;
      seg.rotation = this.data.xform.rotation;
    }
    return this.parent.update(this.parent.data);
  };
  getXform = (): layout.LayoutXform => {
    // Return a copy of the live parent data so callers that snapshot the
    // original xform (e.g. MoveLayoutXformable) don't hold a stale reference
    // that mutates in place.
    const liveSeg = this.parent.data.place.segments[this.index];
    if (liveSeg) {
      return {
        loc: { ...liveSeg.loc },
        reflect: { ...liveSeg.reflect },
        rotation: liveSeg.rotation,
      };
    }
    return {
      loc: { ...this.data.xform.loc },
      reflect: { ...this.data.xform.reflect },
      rotation: this.data.xform.rotation,
    };
  };
  updateXform = (xform: layout.LayoutXform): RedrawRequest => {
    // Update the live parent segments array so rendering picks up the change.
    const seg = this.parent.data.place.segments[this.index];
    if (seg) {
      seg.loc = xform.loc;
      seg.reflect = xform.reflect;
      seg.rotation = xform.rotation;
    }
    this.data.xform = xform;
    return this.parent.update(this.parent.data);
  };
  redraw = (to: DrawTo): unknown => {
    const entity = this.scalarEntity;
    const { layout: lay } = entity.data.target;
    const root = entity.root;

    // Read the live xform directly without the defensive per-field copy that
    // `getXform()` does. `redraw` uses the xform read-only and synchronously,
    // so it doesn't need a snapshot — skipping the copy saves 3 allocations
    // per segment per frame, which adds up on arrays with many segments.
    const liveXform =
      this.parent.data.place.segments[this.index] ?? this.data.xform;
    const orientation = convertSomeOrientation(liveXform);
    const liveSchXform = {
      ...liveXform.loc,
      reflected: orientation.reflected,
      rotation: orientation.rotation,
    };

    // Early-out if this segment is entirely off-screen.
    // Check against the DrawTo viewport before drawing the sprite or any
    // overlays. This mirrors the culling check in ScalarInstanceEntity.redraw()
    // and avoids sprite cache lookups, overlay drawing, and drawPinHighlights
    // for off-screen segments.
    //
    // Parent `InstanceEntity.redraw` has bumped `segments.total` (and the
    // matching per-source `.array.total` or `.freeform.total`) for this
    // segment but *not* `drawn`/`offScreen` — so each segment bumps its
    // own per-frame status here, which keeps the invariants:
    //   total === drawn + offScreen
    //   total === array.total + freeform.total
    // regardless of whether we arrive via the array fast path or the
    // non-array fallback. The per-source bucket is chosen from the parent
    // `SegmentedInstanceEntity.fromArray` flag.
    const bucket = this.parent.fromArray ? "array" : "freeform";
    if (to.viewport) {
      const xformedBbox = model.Xform.transformBbox(liveSchXform, lay.abs.bbox);
      if (!model.Bbox.intersects(xformedBbox, to.viewport)) {
        if (to.stats) {
          to.stats.segments.offScreen += 1;
          to.stats.segments[bucket].offScreen += 1;
        }
        return null;
      }
    }
    if (to.stats) {
      to.stats.segments.drawn += 1;
      to.stats.segments[bucket].drawn += 1;
    }

    // Abstract floorplan mode. Hash on the parent's name so all segments
    // (or array elements) of one instance share a color, instead of each
    // segment hashing its `<parent>.<index>` suffixed name independently.
    if (root.editor.showAbstractView) {
      return entity.redrawAbstract(to, lay, this.parent.data.name);
    }

    // Draw the instantiated layout for every segment.
    // `to.instance()` returns *only* its own cull status — nested instance
    // calls inside the sprite/vector do not propagate. If it was culled
    // (for any reason), skip the per-segment overlays since there's
    // nothing visible on screen to annotate.
    const drawn = to.instance(
      lay,
      spriteBbox(lay),
      liveSchXform,
      entity.layoutEntity.redraw
    );

    // If the top-level instance wasn't drawn, skip further processing for this segment since none of it is visible.
    if (drawn !== "drawn") return null;

    // Draw outline overlay per segment when outlines are enabled and outline is set
    const outline = lay.abs.outline;
    if (root.editor.showOutlines && outline) {
      to.group(liveSchXform, (to) => {
        to.bbox(outline, {
          ...OUTLINE_STYLE,
          linewidth: OUTLINE_STYLE.linewidth / to.scale,
        });
      });
    }
    if (to.layoutDisplay?.instanceLabels) {
      to.overlay("labels", (to) => {
        // Skip labels that would render smaller than the legibility threshold.
        if (entity.cache.style.size * to.scale < MIN_LABEL_SCREEN_PX) {
          to.overlaySizeCulled();
          return;
        }
        to.group(liveSchXform, (to) => {
          to.text(entity.labelData(), entity.cache.style, entity);
        });
      });
    }
    // Draw highlight overlay using live xform
    if (entity.highlighted) {
      to.group(liveSchXform, (to) => {
        to.bbox(lay.abs.bbox, {
          ...HIGHLIGHT_STYLE,
          linewidth: HIGHLIGHT_STYLE.linewidth / to.scale,
        });
      });
    } else if (entity.siblingHighlighted_) {
      // Sibling segment: belongs to same generator but not actively selected
      to.group(liveSchXform, (to) => {
        to.bbox(lay.abs.bbox, {
          ...SIBLING_HIGHLIGHT_STYLE,
          linewidth: SIBLING_HIGHLIGHT_STYLE.linewidth / to.scale,
        });
      });
    }
    // Draw pin highlight markers for any highlighted pins (e.g., for LVS errors)
    drawPinHighlights(to, liveSchXform, lay.abs.pins, entity.highlightedPins);
    return null;
  };
  hitTest = (mousePos: MousePos): boolean => {
    // Use live xform from parent data (not the stale scalarEntity.data).
    // Convert to schematic xform so reflect flags are honored — without
    // this, reflected segments' hitboxes don't match their visual position.
    const xform = this.getXform();
    const { layout: lay } = this.scalarEntity.data.target;
    const schXform = layout.LayoutXform.toXform(xform);
    const bbox = model.Xform.transformBbox(schXform, lay.abs.bbox);
    return s.Bbox.contains(bbox, mousePos.sch);
  };
}

// Bound Scalar Instances
type ScalarInstance = layout.ScalarInstance & { target: { kind: "bound" } };

export class ScalarInstanceEntity extends Entity<ScalarInstance, LayoutRender> {
  constructor(
    args: Args<ScalarInstance, LayoutRender>,
    readonly hierLevel: number
  ) {
    super(args);
    const { target } = this.data;
    // Use the shared LayoutEntity cache on LayoutRender to deduplicate
    // entity trees across instances of the same layout. This reduces
    // entity count from O(instances) to O(unique layouts).

    // Safety: ScalarInstanceEntity is only created within LayoutRender,
    // so this.root is guaranteed to be LayoutRender
    const root = this.root;
    this.layoutEntity = root.getOrCreateLayoutEntity(
      target.layout,
      this.hierLevel,
      this
    );
  }
  readonly layoutEntity: LayoutEntity;

  // Produce the `data` for our instance-name label
  readonly labelData = (): InstanceNameLabel => {
    const { target } = this.data;
    const { layout } = target;
    const bbox = layout.abs.bbox;
    // Position label outside instance (to the left), with top of text aligned to top of bbox
    const loc = {
      x: bbox.min.x - 100, // Slightly left of instance
      y: bbox.max.y, // Top of instance
    };
    return {
      ...graphics.Text.new({
        text: this.data.name, // Instance name
        loc,
        alignment: "right", // Text extends to the left from anchor point
        baseline: "top", // Text extends downward from anchor point
        rotation: 0,
        height: 2000,
        hidden: false,
      }),
      labelKind: s.ExprKind.InstName,
    };
  };

  // Shallow highlight: only set our own highlighted state, do NOT recurse into children.
  // The child tree under a ScalarInstanceEntity is the nested LayoutEntity hierarchy,
  // which can be 33 levels deep. That content is rendered via sprite caching, so
  // highlighting it has no visual effect and causes the browser to lock up.
  // Selection is shown via the overlay rectangle drawn in redraw() instead.
  override highlight(state: boolean = true): RedrawRequest {
    this.highlighted_ = state;
    this.cache.invalidate();
    return this.root.requestRedraw();
  }

  // Whether this entity is a sibling of a selected segment (purple highlight).
  // Set by InstanceEntity.redraw() when per-segment selection is active.
  siblingHighlighted_: boolean = false;

  // Set highlight state for per-segment selection.
  // Called by InstanceEntity.redraw() to propagate selection to individual segments.
  setSegmentHighlight(highlighted: boolean, siblingHighlighted: boolean): void {
    this.highlighted_ = highlighted;
    this.siblingHighlighted_ = siblingHighlighted;
  }

  // Pins to highlight (by pin name, e.g., "d", "s", "g")
  readonly highlightedPins = new Set<string>();

  // Highlight a specific pin on this instance
  highlightPin = (pinName: string): RedrawRequest => {
    this.highlightedPins.add(pinName);
    this.cache.invalidate();
    return this.root.requestRedraw();
  };

  // Clear all pin highlights on this instance
  clearPinHighlights = (): RedrawRequest => {
    this.highlightedPins.clear();
    this.cache.invalidate();
    return this.root.requestRedraw();
  };

  override update = (data: Partial<ScalarInstance>): RedrawRequest => {
    this.data = { ...this.data, ...data };
    // FIXME: handle swaps between bound and unbound targets. Currently stuck in one or the other forever here!
    this.layoutEntity.update(this.data.target.layout);
    return this.root.requestRedraw();
  };

  override readonly cache = this.makeCache<style.TextStyle>(table);
  readonly abstractNameCache =
    this.makeCache<style.TextStyle>(ABSTRACT_NAME_TABLE);

  override redraw = (to: DrawTo): unknown => {
    const { layout } = this.data.target;
    const root = this.root;

    // Early-out if this instance is entirely off-screen.
    // We check against the DrawTo viewport (the visible region in schematic coords)
    // before drawing the sprite or any overlays. This mirrors the culling check
    // inside to.instance() but lets us also skip routes, paths, highlights and labels.
    if (to.viewport) {
      const xformedBbox = model.Xform.transformBbox(
        this.xform,
        layout.abs.bbox
      );
      if (!model.Bbox.intersects(xformedBbox, to.viewport)) {
        return null;
      }
    }

    // Abstract floorplan mode: colored bbox + centered name, no layout detail.
    // Used by the LM-based judge for visual placement checks.
    if (root.editor.showAbstractView) {
      return this.redrawAbstract(to, layout, this.data.name);
    }

    // Draw the main event: the instantiated layout (sprite-cached).
    // `to.instance()` returns *only* its own cull status — nested instance
    // calls inside the sprite/vector do not propagate. If it was culled
    // (for any reason), skip the per-instance overlays since there's
    // nothing visible on screen to annotate.
    const drawn = to.instance(
      layout,
      spriteBbox(layout),
      this.xform,
      this.layoutEntity.redraw
    );
    if (drawn !== "drawn") return null;

    // Draw routes and path elements as overlays outside the sprite so they remain
    // visible at all zoom levels. These are thin (nanometer-scale widths) and
    // disappear when baked into sprites at low zoom. Drawing them here, outside
    // the sprite cache, lets us enforce a minimum screen-pixel width.
    const { implEntity } = this.layoutEntity;
    if (implEntity) {
      // Inverse-transform the schematic viewport into this cell's local
      // coordinates once, so routes and path elements can cull individual
      // off-screen pieces when we're zoomed into part of a large cell.
      const localViewport = to.viewport
        ? model.Xform.inverse(this.xform).applyToBbox(to.viewport)
        : null;
      to.overlay("routes", (to) =>
        to.group(this.xform, (to) => {
          implEntity.routes.redraw(to, localViewport);
          implEntity.redrawPathElements(to, localViewport);
        })
      );
    }

    // Draw highlight overlay on top of sprite when selected.
    // This bypasses the sprite cache so highlighting is always visible
    // without needing to invalidate cached sprites.
    if (this.highlighted) {
      to.group(this.xform, (to) => {
        to.bbox(layout.abs.bbox, {
          ...HIGHLIGHT_STYLE,
          linewidth: HIGHLIGHT_STYLE.linewidth / to.scale,
        });
      });
    }

    // Draw pin highlight markers for any highlighted pins (e.g., for LVS errors)
    drawPinHighlights(to, this.xform, layout.abs.pins, this.highlightedPins);

    // Draw outline overlay when outlines are enabled and the target cell has an outline set.
    // Outline is in local cell coords, so we draw inside the instance's xform group.
    const outline = layout.abs.outline;
    if (root.editor.showOutlines && outline) {
      to.group(this.xform, (to) => {
        to.bbox(outline, {
          ...OUTLINE_STYLE,
          linewidth: OUTLINE_STYLE.linewidth / to.scale,
        });
      });
    }

    // And then any ancillary things, like our instance name label
    if (to.layoutDisplay?.instanceLabels) {
      return to.overlay("labels", (to) => {
        // Skip labels that would render smaller than the legibility threshold.
        if (this.cache.style.size * to.scale < MIN_LABEL_SCREEN_PX) {
          to.overlaySizeCulled();
          return null;
        }
        return to.group(this.xform, (to) => {
          to.text(this.labelData(), this.cache.style, this);
        });
      });
    }
    return null;
  };

  // Abstract floorplan rendering: colored bbox + centered instance name.
  // Picks a color from the rotating palette based on a simple name hash.
  // `colorName` is the identity used for color assignment — callers pass
  // the instance name (not a per-segment `<parent>.<index>`) so all segments
  // of one instance share a color.
  redrawAbstract = (
    to: DrawTo,
    layout: model.layout.Layout,
    colorName: string
  ): unknown => {
    const bbox = layout.abs.bbox;
    const colorIndex = simpleHash(colorName) % ABSTRACT_PALETTE.length;
    const fill = ABSTRACT_PALETTE[colorIndex];
    const stroke = ABSTRACT_BORDER_PALETTE[colorIndex];

    to.group(this.xform, (to) => {
      to.bbox(bbox, {
        fill,
        stroke,
        linewidth: 2 / to.scale,
        cap: "butt",
        join: "miter",
      });
    });

    // Draw centered instance name, scaled to fit the bbox.
    // The canvas renderer uses style.size (not data.height) for font sizing,
    // so we compute a dynamic size based on the instance dimensions and pass
    // it directly in the style object.
    return to.group(this.xform, (to) => {
      const centerX = (bbox.min.x + bbox.max.x) / 2;
      const centerY = (bbox.min.y + bbox.max.y) / 2;
      const bboxWidth = bbox.max.x - bbox.min.x;
      const bboxHeight = bbox.max.y - bbox.min.y;
      // Scale font to fit this entity's bbox.
      const fontSize = Math.min(bboxWidth / this.data.name.length, bboxHeight);
      to.text(
        graphics.Text.new({
          text: this.data.name,
          loc: { x: centerX, y: centerY },
          alignment: "center",
          baseline: "middle",
          rotation: 0,
          height: fontSize,
          hidden: false,
        }),
        { ...this.abstractNameCache.style, size: fontSize },
        this
      );
    });
  };

  // Gotta convert into schematic-style transforms here,
  // particularly so that children (particularly text ones)
  // understand our orientation.
  override get xform(): s.Xform {
    const xform = this.data.place.xform;
    const { x, y } = xform.loc;
    const orientation = convertSomeOrientation(xform);
    return {
      x,
      y,
      reflected: orientation.reflected,
      rotation: orientation.rotation,
    };
  }

  // `LayoutXformable` interface used by GUI moves
  getXform(): layout.LayoutXform {
    return this.data.place.xform;
  }
  updateXform(xform: layout.LayoutXform): RedrawRequest {
    return this.update({ place: { kind: "scalar", xform } });
  }

  hitTest = (mousePos: MousePos): boolean => {
    const { layout } = this.data.target;
    const bbox = s.Xform.transformBbox(this.xform, layout.abs.bbox);
    return s.Bbox.contains(bbox, mousePos.sch);
  };

  readonly toData = (): ScalarInstance => this.data;
}

// Convert layout reflect flags + rotation into a schematic-style Orientation.
//
// This is the single source of truth for how LayoutXform maps to (reflected, rotation).
// Both Segment.redraw and ScalarInstanceEntity.xform MUST use this function.
// Previous regressions occurred when these code paths computed orientation independently
// (e.g. the xform getter only checked reflect.vert, ignoring reflect.horiz entirely).
// Any new code that needs an Orientation from a LayoutXform should call this — do not
// inline the logic or handle only a subset of the reflect/rotation combinations.
function convertSomeOrientation(
  layoutOrient: layout.LayoutXform
): s.Orientation {
  const horiz = layoutOrient.reflect.horiz;
  const vert = layoutOrient.reflect.vert;
  const xformRotation = layoutOrient.rotation ?? 0;

  let reflected: boolean;
  let baseRotation: Rotation;

  switch (vert) {
    case false:
      switch (horiz) {
        case false:
          reflected = false;
          baseRotation = 0;
          break;
        case true:
          reflected = true;
          baseRotation = 180;
          break;
        default:
          exhaust(horiz);
      }
      break;
    case true:
      switch (horiz) {
        case false:
          reflected = true;
          baseRotation = 0;
          break;
        case true:
          reflected = false;
          baseRotation = 180;
          break;
        default:
          exhaust(horiz);
      }
      break;
    default:
      exhaust(vert);
  }

  const rotation = Rotation.sum(baseRotation, xformRotation);
  return { reflected, rotation };
}

type InstanceNameLabel = graphics.Text & {
  labelKind: s.ExprKind;
};

/**
 * Compute the visible row/column range of an array-instance grid within a
 * viewport, so callers can iterate only the on-screen segments instead of
 * all `nrows * ncols` of them.
 *
 * ## Coordinate system and assumptions
 *
 * All inputs and math are in the **parent cell's local coordinate system** —
 * the same system the array instance's `baseLoc`, `dx`, `dy`, and its target
 * cell's `elemBbox` are expressed in. The caller is responsible for passing
 * a `viewport` that has already been transformed into this local frame (see
 * `InstanceEntity.redraw()` for how that's done via `Xform.inverse`).
 *
 * Assumptions:
 * - The grid is regular: segment (row=i, col=j) is placed at
 *   `(baseLoc.x + j*dx, baseLoc.y + i*dy)` and occupies `elemBbox` in its
 *   own local coords.
 * - Row-major storage: `segments[row * ncols + col]`.
 * - `nrows` and `ncols` are positive. `dx` and `dy` may be zero (degenerate
 *   column/row), positive (grid extends right/up), or negative (grid extends
 *   left/down). All cases are handled.
 * - The caller clamps iteration to `segments.length` (because the clamped
 *   range is a superset of legal indices, never a subset).
 *
 * ## Return value: empty ranges
 *
 * Returns `{ rowMin, rowMax, colMin, colMax }` with both values inclusive.
 * An **empty range** is encoded as `rowMax < rowMin` (specifically
 * `{rowMin: 0, rowMax: -1, ...}`), so the standard loop
 *     for (let i = rowMin; i <= rowMax; i++) { ... }
 * executes zero iterations. Two situations produce empty ranges:
 * 1. `dx === 0` or `dy === 0` and the stacked columns/rows fall entirely
 *    outside the viewport on that axis — the whole array is off-screen
 *    and we short-circuit to an empty range covering *both* axes.
 * 2. After clamping to `[0, nrows-1]` / `[0, ncols-1]`, a range that
 *    started outside the valid grid collapses (e.g. `colMin = 5, colMax
 *    = ncols - 1 = 3` → still works: the clamp makes `colMax < colMin`).
 *
 * ## Why the rounding is conservative
 *
 * `Math.floor` on the low end and `Math.ceil` on the high end **over-estimate**
 * by up to one cell on each side. This is deliberate:
 * - A segment straddling the viewport edge (part in, part out) must be
 *   drawn, not culled.
 * - Floating-point error in `(viewport.x - baseLoc.x - ...) / dx` could
 *   put an edge case on the wrong side of an integer boundary. The
 *   conservative rounding guarantees we never drop a visible segment.
 *
 * The cost is up to one extra row and one extra column of invisible
 * segments drawn per frame — cheap compared to iterating every segment.
 *
 * Indices returned may fall outside `[0, ncols-1]` / `[0, nrows-1]` — the
 * final clamp before return brings them back into the valid grid range.
 */
function arrayVisibleSegments(
  grid: ArrayGrid,
  viewport: model.Bbox
): { rowMin: number; rowMax: number; colMin: number; colMax: number } {
  const { nrows, ncols, dx, dy, baseLoc, elemBbox } = grid;

  // Compute column range from viewport X bounds.
  // Each column j places an element at x = baseLoc.x + j*dx with width elemW.
  // We need to find the range of j where [baseLoc.x + j*dx + elemBbox.min.x,
  // baseLoc.x + j*dx + elemBbox.max.x] intersects [viewport.min.x, viewport.max.x].
  let colMin: number;
  let colMax: number;
  if (dx !== 0) {
    // Solve for j: baseLoc.x + j*dx + elemBbox.min.x <= viewport.max.x
    //          and: baseLoc.x + j*dx + elemBbox.max.x >= viewport.min.x
    const jFromRight = (viewport.max.x - baseLoc.x - elemBbox.min.x) / dx;
    const jFromLeft = (viewport.min.x - baseLoc.x - elemBbox.max.x) / dx;
    if (dx > 0) {
      colMin = Math.floor(jFromLeft);
      colMax = Math.ceil(jFromRight);
    } else {
      // Negative dx: columns go right-to-left
      colMin = Math.floor(jFromRight);
      colMax = Math.ceil(jFromLeft);
    }
  } else {
    // dx === 0: all columns stack at the same X; either all visible or none
    const x0 = baseLoc.x + elemBbox.min.x;
    const x1 = baseLoc.x + elemBbox.max.x;
    if (x1 < viewport.min.x || x0 > viewport.max.x) {
      return { rowMin: 0, rowMax: -1, colMin: 0, colMax: -1 }; // empty range
    }
    colMin = 0;
    colMax = ncols - 1;
  }

  // Compute row range from viewport Y bounds.
  // Each row i places an element at y = baseLoc.y + i*dy with height elemH.
  let rowMin: number;
  let rowMax: number;
  if (dy !== 0) {
    const iFromTop = (viewport.max.y - baseLoc.y - elemBbox.min.y) / dy;
    const iFromBottom = (viewport.min.y - baseLoc.y - elemBbox.max.y) / dy;
    if (dy > 0) {
      rowMin = Math.floor(iFromBottom);
      rowMax = Math.ceil(iFromTop);
    } else {
      // Negative dy: rows go bottom-to-top
      rowMin = Math.floor(iFromTop);
      rowMax = Math.ceil(iFromBottom);
    }
  } else {
    // dy === 0: all rows stack at the same Y; either all visible or none
    const y0 = baseLoc.y + elemBbox.min.y;
    const y1 = baseLoc.y + elemBbox.max.y;
    if (y1 < viewport.min.y || y0 > viewport.max.y) {
      return { rowMin: 0, rowMax: -1, colMin: 0, colMax: -1 }; // empty range
    }
    rowMin = 0;
    rowMax = nrows - 1;
  }

  // Clamp to valid grid bounds
  colMin = Math.max(0, colMin);
  colMax = Math.min(ncols - 1, colMax);
  rowMin = Math.max(0, rowMin);
  rowMax = Math.min(nrows - 1, rowMax);

  return { rowMin, rowMax, colMin, colMax };
}
